import React from 'react';
import { Badge, Tag, Tooltip } from 'antd';
import { CrownFilled } from '@ant-design/icons';
import styled from 'styled-components';

import {
  ASSET_PRESETS,
  IOperator,
  assetTypeOf,
  colorOf,
} from '../../../types/models/tenancy';

const Card = styled.div<{ $selected: boolean }>`
  border: 1px solid ${p => (p.$selected ? 'var(--ancana-gold)' : 'var(--ancana-lightergray)')};
  background: ${p => (p.$selected ? 'var(--ancana-lightestgray)' : 'transparent')};
  border-radius: 3px;
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--ancana-gold);
  }

  .mark {
    width: 34px;
    height: 34px;
    border-radius: 3px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .body {
    min-width: 0;
    flex: 1;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    line-height: 1.3;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .slug {
    font-family: monospace;
    font-size: 11px;
    color: var(--ancana-font-light);
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    flex-wrap: wrap;
    font-size: 11px;
    color: var(--ancana-font-light);
  }

  .swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    border: 1px solid var(--ancana-lightergray);
    display: inline-block;
  }
`;

export interface TenantCardProps {
  operator: IOperator;
  selected: boolean;
  onSelect: () => void;
}

/**
 * One tenant in the roster.
 *
 * Shows the tenant *as configured* rather than as a row of fields: the logo (or
 * a monogram in its own primary color when there is none), the two brand colors
 * as swatches, and what it calls its assets. That is the fastest way to spot the
 * thing this panel exists to catch — a tenant left on platform defaults, or two
 * tenants that look identical to their owners.
 */
const TenantCard = ({ operator, selected, onSelect }: TenantCardProps) => {
  const primary = colorOf(operator, 'primary');
  const surface = colorOf(operator, 'surface');
  const assetType = assetTypeOf(operator);
  const logo = operator.branding?.logo_url;
  const isConfigured = !!operator.branding?.theme_tokens?.colors?.primary;

  return (
    <Card $selected={selected} onClick={onSelect}>
      <div className="mark" style={{ background: logo ? surface : primary, color: '#fff' }}>
        {logo
          ? <img src={logo} alt="" />
          : (operator.brand_name || operator.name || '?').trim().charAt(0).toUpperCase()}
      </div>
      <div className="body">
        <div className="title">
          <span>{operator.brand_name || operator.name}</span>
          {operator.is_platform_owner && (
            <Tooltip title="Platform owner — its staff can reach every tenant.">
              <CrownFilled style={{ color: 'var(--ancana-gold)' }} />
            </Tooltip>
          )}
          {operator.status === 'suspended' && <Tag color="red">Suspended</Tag>}
          {operator.status === 'active' && operator.trial_days_left != null && (
            <Tooltip title={`Trial of ${operator.plan_name || 'a plan'} until ${operator.trial_ends_at}`}>
              <Tag color="blue">Trial · {operator.trial_days_left}d</Tag>
            </Tooltip>
          )}
        </div>
        <div className="slug">{operator.slug}</div>
        <div className="meta">
          <Tooltip title={isConfigured ? `Primary ${primary} · Surface ${surface}` : 'Using the platform default palette'}>
            <span>
              <i className="swatch" style={{ background: primary }} />
              <i className="swatch" style={{ background: surface, marginLeft: 3 }} />
            </span>
          </Tooltip>
          <span>{ASSET_PRESETS[assetType].icon} {ASSET_PRESETS[assetType].label}</span>
          <Badge status={operator.status === 'active' ? 'success' : 'default'} />
          <span>{operator.member_count} staff</span>
        </div>
      </div>
    </Card>
  );
};

export default TenantCard;
